/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState, useEffect, Suspense } from 'react';
import { auth, db, ensureFirebaseAuth } from './lib/firebase';
import { onAuthStateChanged, signInWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, collection, onSnapshot, query, orderBy, where, limit, getDocs } from 'firebase/firestore';
import { UserProfile, ClubEvent, Attendance, RankRequirement } from './types';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { handleFirestoreError, OperationType } from './lib/firebase';
import { sha256Hex } from './lib/utils';
import { 
  Rocket, 
  Users, 
  Calendar, 
  Trophy, 
  Settings, 
  LogOut, 
  LogIn,
  ShieldAlert,
  Menu,
  X
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { isSystemAdmin, isUserManager, isDeptLead } from './lib/admin';
import { RANKS, DEPARTMENTS } from './constants';

import EventList from './components/EventList';
import ProfileView from './components/ProfileView';
import AdminDashboard from './components/AdminDashboard';
import ShipsLogMain from './components/ShipsLog/ShipsLogMain';
import ArchiveViewer from './components/ShipsLog/ArchiveViewer';
import MissionPlanner from './components/MissionPlanner';
import Communications from './components/Communications';
import LoginManager from './components/LoginManager';
import ConPlannerMain from './components/ConPlanner/ConPlannerMain';
import ConResourcesMain from './components/ConPlanner/ConResourcesMain';
import ConVolunteerForm from './components/ConPlanner/ConVolunteerForm';
import ConPublicSchedule from './components/ConPlanner/ConPublicSchedule';
import TimeTracker from './components/TimeTracker';
import HelpReporterModal from './components/HelpReporterModal';
import HowDoIModal from './components/HowDoIModal';
import { HelpCircle } from 'lucide-react';
import LoginAlertsModal from './components/LoginAlertsModal';
import SurveyList from './components/SurveyList';

const APP_VERSION = 'v1.6.0';

import RegistrationForm from './components/RegistrationForm';
import ForcePasswordReset from './components/ForcePasswordReset';
import SimpleDashboard from './components/SimpleMode/SimpleDashboard';
import LCARSVoiceAssistant from './components/VoiceAssistant/LCARSVoiceAssistant';

export default function App() {
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isNewUser, setIsNewUser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('comms');
  const [isSimpleMode, setIsSimpleMode] = useState<boolean>(() => {
    return localStorage.getItem('zavala-simple-mode') === 'true';
  });

  const toggleSimpleMode = (forcedState?: boolean) => {
    setIsSimpleMode(prev => {
      const next = typeof forcedState === 'boolean' ? forcedState : !prev;
      localStorage.setItem('zavala-simple-mode', String(next));
      return next;
    });
  };
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
  const getInitialPath = () => {
    if (window.location.hash && window.location.hash.startsWith('#/')) {
      return window.location.hash.replace('#', '');
    }
    return window.location.pathname;
  };

  const [currentPath, setCurrentPath] = useState(getInitialPath());
  const [selectedConIdForPlanner, setSelectedConIdForPlanner] = useState<string | null>(null);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [theme, setTheme] = useState(localStorage.getItem('zavala-theme') || 'trek-classic');
  const [textSize, setTextSize] = useState(localStorage.getItem('zavala-textsize') || 'normal');
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isHowDoIModalOpen, setIsHowDoIModalOpen] = useState(false);
  const [hasNewAnnouncements, setHasNewAnnouncements] = useState(false);
  const [hasNewTenForward, setHasNewTenForward] = useState(false);

  const [alertsChecked, setAlertsChecked] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [activeAlerts, setActiveAlerts] = useState<{ sfi: boolean; dues: boolean }>({ sfi: false, dues: false });

  useEffect(() => {
    if (profile) {
      if (!alertsChecked) {
        const today = new Date();
        const currentYear = today.getFullYear();
        const isDecember = today.getMonth() === 11; // 11 is December

        let hasSfiAlert = false;
        if (profile.sfiExpireDate) {
          const sfiDate = new Date(profile.sfiExpireDate);
          const diffTime = sfiDate.getTime() - today.getTime();
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          if (diffDays <= 30) {
            const alreadyDismissed = localStorage.getItem(`dismissed_sfi_reminder_${profile.sfiExpireDate}`) === 'true';
            if (!alreadyDismissed) {
              hasSfiAlert = true;
            }
          }
        }

        let hasDuesAlert = false;
        if (isDecember) {
          const duesYear = profile.duesPaidYear;
          if (duesYear !== currentYear) {
            const alreadyDismissed = localStorage.getItem(`dismissed_dues_reminder_${currentYear}`) === 'true';
            if (!alreadyDismissed) {
              hasDuesAlert = true;
            }
          }
        }

        if (hasSfiAlert || hasDuesAlert) {
          setActiveAlerts({ sfi: hasSfiAlert, dues: hasDuesAlert });
          setShowAlertModal(true);
        }
        setAlertsChecked(true);
      }
    } else {
      setAlertsChecked(false);
      setShowAlertModal(false);
      setActiveAlerts({ sfi: false, dues: false });
    }
  }, [profile, alertsChecked]);

  const handleDismissAlert = (type: 'sfi' | 'dues', permanently: boolean) => {
    if (!profile) return;
    
    if (type === 'sfi') {
      if (permanently && profile.sfiExpireDate) {
        localStorage.setItem(`dismissed_sfi_reminder_${profile.sfiExpireDate}`, 'true');
      }
      setActiveAlerts(prev => {
        const updated = { ...prev, sfi: false };
        if (!updated.sfi && !updated.dues) {
          setShowAlertModal(false);
        }
        return updated;
      });
    } else if (type === 'dues') {
      if (permanently) {
        const currentYear = new Date().getFullYear();
        localStorage.setItem(`dismissed_dues_reminder_${currentYear}`, 'true');
      }
      setActiveAlerts(prev => {
        const updated = { ...prev, dues: false };
        if (!updated.sfi && !updated.dues) {
          setShowAlertModal(false);
        }
        return updated;
      });
    }
  };

  // Check for new communications
  useEffect(() => {
    if (!profile) return;
    
    const getReferenceTime = (key: string) => {
      const stored = localStorage.getItem(key);
      if (stored) return parseInt(stored, 10);
      return 0; // If they've never visited, show the indicator if there's any content
    };

    const qA = query(collection(db, 'announcements'), orderBy('timestamp', 'desc'), limit(10));
    const unsubA = onSnapshot(qA, (snap) => {
      const regularAnnouncements = snap.docs.filter(d => d.data().type !== 'release_note');
      if (regularAnnouncements.length > 0) {
        const latestTime = new Date(regularAnnouncements[0].data().timestamp).getTime();
        setHasNewAnnouncements(latestTime > getReferenceTime('lastViewedAnnouncements'));
      }
    });

    const qT = query(collection(db, 'messages'), where('channelId', '==', 'general'), orderBy('timestamp', 'desc'), limit(1));
    const unsubT = onSnapshot(qT, (snap) => {
      if (!snap.empty) {
        const latestTime = new Date(snap.docs[0].data().timestamp).getTime();
        setHasNewTenForward(latestTime > getReferenceTime('lastViewedTenForward'));
      }
    });

    return () => { unsubA(); unsubT(); };
  }, [profile]);

  useEffect(() => {
    const handleNavigation = () => setCurrentPath(getInitialPath());
    window.addEventListener('popstate', handleNavigation);
    window.addEventListener('hashchange', handleNavigation);
    return () => {
      window.removeEventListener('popstate', handleNavigation);
      window.removeEventListener('hashchange', handleNavigation);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('zavala-theme', theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem('zavala-textsize', textSize);
    if (textSize === 'large') {
      document.documentElement.style.fontSize = '18px';
    } else if (textSize === 'xlarge') {
      document.documentElement.style.fontSize = '20px';
    } else {
      document.documentElement.style.fontSize = '';
    }
  }, [textSize]);

  // Global Data
  const [allUsers, setAllUsers] = useState<UserProfile[]>([]);
  const [allEvents, setAllEvents] = useState<ClubEvent[]>([]);
  const [allAttendance, setAllAttendance] = useState<Attendance[]>([]);
  const [allRanks, setAllRanks] = useState<RankRequirement[]>([]);
  const [allDepartments, setAllDepartments] = useState<{id: string, name: string}[]>([]);

  // Profile loading and snapshot setup
  useEffect(() => {
    if (!user) {
      setProfile(null);
      return;
    }

    let unsubProfile: () => void;
    let isUnsubscribed = false;

    const loadProfile = async () => {
      try {
        let userDocId = user.uid; // Default to uid
        
        // 1. Direct document check first: fastest and standard path for Google Auth users
        try {
          const directSnap = await getDoc(doc(db, 'users', user.uid));
          if (directSnap.exists()) {
            userDocId = user.uid;
          } else if (user.email) {
            // 2. If no doc by UID, attempt email lookup to match any pre-imported roster accounts
            try {
              const emailTrimmed = user.email.trim();
              const q1 = query(collection(db, 'users'), where('email', '==', emailTrimmed));
              const snap1 = await getDocs(q1);
              if (!snap1.empty) {
                userDocId = snap1.docs[0].id;
              } else {
                const q2 = query(collection(db, 'users'), where('email', '==', emailTrimmed.toLowerCase()));
                const snap2 = await getDocs(q2);
                if (!snap2.empty) {
                  userDocId = snap2.docs[0].id;
                }
              }
            } catch (queryErr) {
              console.warn("Could not query users by email (new accounts may not have collection query permissions):", queryErr);
            }
          }
        } catch (docErr) {
          console.warn("Direct doc read check:", docErr);
        }

        if (isUnsubscribed) return;

        const docRef = doc(db, 'users', userDocId);
        unsubProfile = onSnapshot(docRef, (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data() as any;
            data.uid = docSnap.id; // Ensure uid matches the document ID
            
            // Check if user is inactive
            if (data.status === 'inactive') {
              toast.error('ACCESS DENIED: Personnel file is marked inactive. Contact Command for assistance.', { duration: 10000 });
              if (localStorage.getItem('lcars_custom_session')) {
                localStorage.removeItem('lcars_custom_session');
              } else {
                signOut(auth);
              }
              setUser(null);
              setProfile(null);
              setIsNewUser(false);
              setLoading(false);
              return;
            }

            // Check if membership is expired (excluding system administrators)
            if (data.zavalaExpireDate && !isSystemAdmin(data)) {
              const localDate = new Date();
              const year = localDate.getFullYear();
              const month = String(localDate.getMonth() + 1).padStart(2, '0');
              const day = String(localDate.getDate()).padStart(2, '0');
              const localDateStr = `${year}-${month}-${day}`;
              if (data.zavalaExpireDate < localDateStr) {
                toast.error('ACCESS DENIED: Your USS Zavala membership has expired. Please contact an administrator to renew your credentials.', { duration: 10000 });
                if (localStorage.getItem('lcars_custom_session')) {
                  localStorage.removeItem('lcars_custom_session');
                } else {
                  signOut(auth);
                }
                setUser(null);
                setProfile(null);
                setIsNewUser(false);
                setLoading(false);
                return;
              }
            }

            // Migration: if roles is missing but role exists
            if (!data.roles && data.role) {
              data.roles = [data.role];
            } else if (!data.roles) {
              data.roles = ['member'];
            }
            setProfile(data as UserProfile);
            setIsNewUser(false);
          } else {
            // New user setup (document does not exist yet)
            setIsNewUser(true);
          }
          setLoading(false);
        }, (error) => {
          console.warn("Profile listener status:", error);
          // If the profile document doesn't exist yet or rules require registration, show registration form
          setIsNewUser(true);
          setLoading(false);
        });
      } catch (err) {
        console.error("Profile initialization fallback:", err);
        setIsNewUser(true);
        setLoading(false);
      }
    };

    loadProfile();

    return () => {
      isUnsubscribed = true;
      if (unsubProfile) {
        unsubProfile();
      }
    };
  }, [user]);

  // Auth/Session listener setup
  useEffect(() => {
    // 1. Check for custom session on mount
    const customSessionStr = localStorage.getItem('lcars_custom_session');
    if (customSessionStr) {
      try {
        const customUser = JSON.parse(customSessionStr);
        ensureFirebaseAuth().catch(e => console.error("Failed custom session firebase auth", e));
        setUser(customUser);
        setLoading(false);
      } catch (e) {
        console.error('Failed to parse custom session', e);
      }
    }

    const unsubscribeAuth = onAuthStateChanged(auth, async (u) => {
      // If there's an active custom session, ignore Firebase auth's null events
      if (localStorage.getItem('lcars_custom_session')) {
        return;
      }
      setUser(u);
      if (!u) {
        setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
    };
  }, []);

  // Background Database Migration for Default Password
  useEffect(() => {
    if (!profile) return;
    const isSysAdmin = isSystemAdmin(profile);
    const isUserMgr = isUserManager(profile);
    if ((isSysAdmin || isUserMgr) && allUsers.length > 0) {
      const usersWithoutPassword = allUsers.filter(u => !u.password && u.uid);
      if (usersWithoutPassword.length > 0) {
        console.log(`Running migration: setting default password Z@v@l@ for ${usersWithoutPassword.length} users.`);
        usersWithoutPassword.forEach(async (u) => {
          try {
            const userRef = doc(db, 'users', u.uid);
            await setDoc(userRef, {
              password: 'Z@v@l@',
              passwordLastChanged: new Date().toISOString()
            }, { merge: true });
          } catch (e) {
            console.error(`Failed to migrate user ${u.uid}`, e);
          }
        });
      }
    }
  }, [allUsers, profile]);

  useEffect(() => {
    if (!user?.uid || isViewOnly) return;
    
    const updateActive = () => {
      try {
        setDoc(doc(db, 'users', user.uid), { lastActive: new Date().toISOString() }, { merge: true });
      } catch (e) {
        console.error('Failed to update active status', e);
      }
    };
    
    updateActive();
    const interval = setInterval(updateActive, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user?.uid, isViewOnly]);

  useEffect(() => {
    if (!user || !profile) return;

    const isGuestUser = profile?.roles?.includes('guest');
    const isSysAdmin = isSystemAdmin(profile);
    const isDeptLd = isDeptLead(profile);

    // Only system admins and user managers can query all users. Others must query only their own.
    const canListAllUsers = isSysAdmin || isUserManager(profile);
    const qUsers = canListAllUsers
      ? query(collection(db, 'users'), orderBy('lastName'))
      : query(collection(db, 'users'), where('uid', '==', user.uid));

    const unsubUsers = onSnapshot(qUsers, (snap) => {
      setAllUsers(snap.docs.map(d => ({ id: d.id, ...d.data() } as UserProfile)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'users');
    });
    
    const qEvents = query(collection(db, 'events'), orderBy('startDate', 'desc'));

    const unsubEvents = onSnapshot(qEvents, (snap) => {
      const eventsData = snap.docs.map(d => ({ id: d.id, ...d.data() } as ClubEvent));
      setAllEvents(eventsData);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'events');
    });

    // Only department leads and admins can list all attendance
    const qAttendance = (isDeptLd || isSysAdmin)
      ? query(collection(db, 'attendance'))
      : query(collection(db, 'attendance'), where('userId', '==', user.uid));

    const unsubAttendance = onSnapshot(qAttendance, (snap) => {
      setAllAttendance(snap.docs.map(d => ({ id: d.id, ...d.data() } as Attendance)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'attendance');
    });

    const qRanks = query(collection(db, 'ranks'), orderBy('order'));
    const unsubRanks = onSnapshot(qRanks, (snap) => {
      const ranksData = snap.docs.map(d => ({ id: d.id, ...d.data() } as RankRequirement));
      if (ranksData.length > 0) {
        setAllRanks(ranksData);
      } else {
        setAllRanks(RANKS);
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'ranks');
    });

    const unsubDepartments = onSnapshot(collection(db, 'departments'), (snap) => {
      const deptsData = snap.docs.map(d => ({ id: d.id, name: d.data().name }));
      if (deptsData.length > 0) {
        // Sort alphabetically
        setAllDepartments(deptsData.sort((a, b) => a.name.localeCompare(b.name)));
      } else {
        // Seed with legacy DEPARTMENTS constant
        setAllDepartments(DEPARTMENTS.map(d => ({ id: d.toLowerCase().replace(/[^a-z0-9]/g, '-'), name: d })));
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'departments');
    });

    return () => {
      unsubUsers();
      unsubEvents();
      unsubAttendance();
      unsubRanks();
      unsubDepartments();
    };
  }, [user, profile]);

  const handleLogout = () => {
    localStorage.removeItem('lcars_custom_session');
    signOut(auth);
    setUser(null);
    setProfile(null);
    setIsViewOnly(false);
    toast.info('Logged out');
  };

  const handleViewOnlyLogin = async (password: string) => {
    // Strategic access key validation via cryptographic hash
    const inputClean = (password || '').trim().toUpperCase();
    const hash = await sha256Hex(inputClean);
    const validHashes = [
      'b48c8732a6e297193cf552a88c9914dd5a83d0d4ff72ad766e0cff703d1369c6', // NCC-74709
      '34eadbb7bca92f5ee9eb127d8765a1ab9aa48f0c4b158a2d7c65e2a9a42133f0'  // LOGS
    ];
    if (validHashes.includes(hash)) {
      setIsViewOnly(true);
      setProfile({
        uid: 'view-only-guest',
        firstName: 'Guest',
        lastName: 'Personnel',
        email: 'guest@starfleet.com',
        currentRank: 'GUEST',
        roles: ['guest'],
        zavalaJoinDate: new Date().toISOString().split('T')[0],
        promotionRankDate: new Date().toISOString().split('T')[0],
        ots: false,
        occ: false,
        sdfpp: false,
        manualPoints: 0
      });
      setActiveTab('comms');
      toast.success('Restricted access granted. Loading ship logs for current cycle.');
    } else {
      toast.error('Security violation: Incorrect encryption key.');
    }
  };

  if (loading || (user && !profile && !isNewUser)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[100dvh] bg-[var(--background)] text-lcars-gold font-lcars">
        <Rocket className="w-16 h-16 animate-pulse mb-4" />
        <div className="text-[0.75rem] uppercase tracking-[0.3em] animate-pulse">Initializing Computer Core...</div>
      </div>
    );
  }

  if (isNewUser) {
    return (
      <>
        <Toaster position="top-right" />
        <Suspense fallback={<div className="flex justify-center p-8 text-lcars-blue animate-pulse">LOADING...</div>}>
          <RegistrationForm 
            user={user} 
            onRegistered={(p) => { 
              setIsNewUser(false); 
              setProfile(p); 
            }} 
          />
        </Suspense>
      </>
    );
  }

  // Public Routing (unauthenticated access)
  if (currentPath.includes('/signup/')) {
    const conId = currentPath.split('/signup/')[1].replace(/\/$/, '').split('?')[0];
    return (
      <div className="min-h-[100dvh] bg-[var(--background)] text-[var(--foreground)] font-lcars p-4 flex flex-col">
        <Toaster position="top-right" />
        <div className="max-w-5xl mx-auto w-full mb-4">
          <button 
            onClick={() => window.location.href = window.location.origin + window.location.pathname}
            className="text-lcars-blue hover:text-lcars-blue/80 font-bold uppercase text-sm flex items-center gap-2 transition-colors"
          >
            ◀ RETURN TO MAIN LCARS TERMINAL
          </button>
        </div>
        <Suspense fallback={<div className="flex justify-center p-8 text-lcars-blue animate-pulse">LOADING...</div>}>
          <ConVolunteerForm conId={conId} currentUserProfile={profile} />
        </Suspense>
      </div>
    );
  }

  if (currentPath.includes('/schedule/')) {
    const conId = currentPath.split('/schedule/')[1].replace(/\/$/, '').split('?')[0];
    return (
      <div className="min-h-[100dvh] bg-[var(--background)] text-[var(--foreground)] font-lcars p-4 flex flex-col">
        <Toaster position="top-right" />
        <div className="max-w-5xl mx-auto w-full mb-4">
          <button 
            onClick={() => window.location.href = window.location.origin + window.location.pathname}
            className="text-lcars-blue hover:text-lcars-blue/80 font-bold uppercase text-sm flex items-center gap-2 transition-colors"
          >
            ◀ RETURN TO MAIN LCARS TERMINAL
          </button>
        </div>
        <Suspense fallback={<div className="flex justify-center p-8 text-lcars-blue animate-pulse">LOADING...</div>}>
          <ConPublicSchedule conId={conId} currentUserProfile={profile} />
        </Suspense>
      </div>
    );
  }

  if (currentPath.includes('/public')) {
    return (
      <div className={`min-h-[100dvh] bg-[var(--background)] text-[var(--foreground)] font-lcars p-4 ${theme === 'trek-classic' ? '' : theme}`}>
        <Toaster position="top-right" />
        <Suspense fallback={<div className="flex justify-center p-8 text-lcars-blue animate-pulse">LOADING...</div>}>
          <TimeTracker />
        </Suspense>
      </div>
    );
  }

  if (!user && !isViewOnly) {
    return (
      <Suspense fallback={<div className="flex items-center justify-center min-h-[100dvh] bg-[var(--background)] text-lcars-blue font-lcars animate-pulse text-2xl tracking-widest">LOADING...</div>}>
        <LoginManager onViewOnlyLogin={handleViewOnlyLogin} onCustomLogin={(customUser: any) => setUser(customUser)} />
      </Suspense>
    );
  }

  const needsPasswordReset = profile && profile.password === 'Z@v@l@';

  if (needsPasswordReset) {
    return (
      <ForcePasswordReset profile={profile!} onLogout={handleLogout} />
    );
  }

  const isUserSystemAdmin = isSystemAdmin(profile);

  const isAdmin = isUserSystemAdmin;
  
  const isUserMgr = isUserManager(profile);

  const isDLead = isDeptLead(profile);

  const isGuest = (profile?.roles?.includes('guest') || isViewOnly) && !isUserSystemAdmin;

  const isOnZavala = Boolean(profile?.shipName?.toLowerCase().includes('zavala')) || isUserSystemAdmin;

  const isConPlanner = !isViewOnly && !profile?.roles?.includes('read_only');

  const hasCoreAdminAccess = isAdmin || isUserMgr;
  const hasAdminAccess = hasCoreAdminAccess; // Specifically for Command Deck / Mission Admin

  const hasInventoryAccess = isAdmin || profile?.roles?.includes('inventory');

  const adminItemsCount = (isDLead ? 1 : 0) + (hasCoreAdminAccess ? 2 : 0) + (hasInventoryAccess ? 1 : 0);
  const hasAnyAdminAccess = adminItemsCount > 0;

  const visibleEvents = allEvents;

  if (isSimpleMode) {
    const nextEvt = allEvents.find(e => e.startDate >= new Date().toISOString().split('T')[0]);
    return (
      <div className="relative min-h-screen bg-[#0d1117]">
        <Toaster position="top-right" />
        <SimpleDashboard
          currentUser={profile}
          events={allEvents}
          attendance={allAttendance}
          ranks={allRanks}
          onNavigateTab={(tab: string) => {
            setIsSimpleMode(false);
            setActiveTab(tab);
          }}
          onToggleSimpleMode={() => toggleSimpleMode(false)}
          onOpenHelp={() => setIsHelpModalOpen(true)}
          onRsvpEvent={async (eventId, status) => {
            if (!user) return;
            try {
              const rsvpDocId = `${user.uid}_${eventId}`;
              await setDoc(doc(db, 'attendance', rsvpDocId), {
                userId: user.uid,
                eventId,
                status,
                timestamp: new Date().toISOString()
              });
              toast.success('RSVP status updated!');
            } catch (err) {
              handleFirestoreError(err, OperationType.WRITE, 'attendance');
            }
          }}
        />
        <LCARSVoiceAssistant
          onNavigateTab={(tab) => {
            setIsSimpleMode(false);
            setActiveTab(tab);
          }}
          onToggleSimpleMode={toggleSimpleMode}
          isSimpleMode={isSimpleMode}
          currentUserRank={allRanks.find(r => r.id === profile?.currentRank)?.name || profile?.currentRank}
          currentUserName={profile ? `${profile.firstName} ${profile.lastName}` : undefined}
          nextEventTitle={nextEvt?.name}
          nextEventDate={nextEvt?.startDate}
        />
        {isHelpModalOpen && (
          <HelpReporterModal 
            onClose={() => setIsHelpModalOpen(false)} 
            currentUserProfile={profile} 
            appVersion={APP_VERSION}
          />
        )}
      </div>
    );
  }

  return (
    <div className={`h-[100dvh] flex flex-col md:flex-row bg-[var(--background)] text-[var(--foreground)] font-lcars overflow-hidden p-1 md:p-2 gap-1 md:gap-2 ${theme === 'trek-classic' ? '' : theme}`}>
      <Toaster position="top-right" />
      
      {/* Mobile Header */}
      <header className="md:hidden h-14 flex items-center gap-1.5 shrink-0 relative">
        <div className="h-full w-16 bg-lcars-purple rounded-r-full flex overflow-hidden">
          <button onClick={() => setIsMenuOpen(true)} className="text-black w-full h-full flex items-center justify-center cursor-pointer active:bg-[var(--background)]/20">
            <Menu className="w-7 h-7" />
          </button>
        </div>
        <div onClick={() => setActiveTab('comms')} className="flex-1 h-full bg-lcars-gold flex flex-col justify-center px-3 cursor-pointer active:opacity-80 transition-opacity">
          <div className="text-black font-bold text-base tracking-tighter truncate leading-none">
            USS ZAVALA | NCC-74709
          </div>
          <div className="text-[0.45rem] text-black/60 font-mono tracking-widest mt-0.5">{APP_VERSION}</div>
        </div>
        <button
          onClick={() => toggleSimpleMode(true)}
          className="bg-[#238636] hover:bg-[#2ea043] text-white px-2.5 py-1.5 rounded-full text-[0.6rem] font-bold tracking-wider uppercase flex items-center gap-1 shadow-sm shrink-0"
        >
          <span>🟢</span> EASY
        </button>
        <div className="h-full w-10 bg-lcars-blue rounded-l-full flex items-center justify-center">
          <div className="w-1.5 h-1.5 bg-[var(--background)] rounded-full animate-pulse"></div>
        </div>
      </header>

      {/* Mobile Navigation Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div 
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-50 bg-[var(--background)] flex flex-col p-4 gap-4 overflow-y-auto"
          >
            <div className="flex justify-between items-center max-w-md mx-auto w-full">
              <div className="text-lcars-gold font-bold text-2xl tracking-tighter">SYSTEM ACCESS</div>
              <button onClick={() => setIsMenuOpen(false)} className="p-2 bg-lcars-red text-[var(--foreground)] rounded-full">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* View Controls Mobile */}
            <div className="flex flex-col gap-2 mt-2 bg-[var(--background)]/10 p-4 rounded-lg border border-lcars-blue/30 max-w-md mx-auto w-full">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-lcars-blue tracking-widest uppercase">Theme</label>
                <select 
                  value={theme}
                  onChange={(e) => setTheme(e.target.value)}
                  className="bg-[var(--background)] text-[var(--foreground)] border-2 border-lcars-blue rounded-md px-3 py-2 text-sm font-bold uppercase tracking-widest outline-none w-full"
                >
                  <option value="trek-classic">Trek Classic</option>
                  <option value="theme-delta">Delta Quadrant</option>
                  <option value="theme-sovereign">Sovereign Protocol</option>
                  <option value="theme-professional">Professional</option>
                  <option value="theme-light">Light Mode</option>
                  <option value="theme-ada">ADA Compliant (Light)</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-lcars-blue tracking-widest uppercase">Text Size</label>
                <select 
                  value={textSize}
                  onChange={(e) => setTextSize(e.target.value)}
                  className="bg-[var(--background)] text-[var(--foreground)] border-2 border-lcars-blue rounded-md px-3 py-2 text-sm font-bold uppercase tracking-widest outline-none w-full"
                >
                  <option value="normal">Normal</option>
                  <option value="large">Large</option>
                  <option value="xlarge">Extra Large</option>
                </select>
              </div>
            </div>
            
            <div className="flex flex-col gap-3 mt-4 max-w-md mx-auto w-full">
              <button 
                className={`h-12 lcars-pill text-right pr-8 text-lg font-bold transition-all ${activeTab === 'comms' ? 'bg-lcars-blue text-black' : 'bg-lcars-gold text-black'}`}
                onClick={() => { setActiveTab('comms'); setIsMenuOpen(false); }}
              >
                HOME / COMMS {(hasNewAnnouncements || hasNewTenForward) && '*'}
              </button>
              <button 
                className={`h-12 lcars-pill text-right pr-8 text-lg font-bold transition-all ${activeTab === 'planner' ? 'bg-lcars-blue text-black' : 'bg-lcars-tan text-black'}`}
                onClick={() => { setActiveTab('planner'); setIsMenuOpen(false); }}
              >
                CALENDAR
              </button>
              {isConPlanner && (
                <>
                  <button 
                    className={`h-12 lcars-pill text-right pr-8 text-lg font-bold transition-all ${activeTab === 'con-planner' ? 'bg-lcars-blue text-black' : 'bg-lcars-gold text-black'}`}
                    onClick={() => { setActiveTab('con-planner'); setIsMenuOpen(false); }}
                  >
                    CON DUTY ROSTER
                  </button>
                </>
              )}

              {/* Admin Group */}
              {hasAnyAdminAccess && isOnZavala && (
                <div className="flex flex-col gap-2 mt-2 pt-2 border-t border-white/10">
                  {adminItemsCount === 1 ? (
                    <>
                      {isDLead && (
                        <button 
                          className={`h-12 lcars-pill text-right pr-8 text-lg font-bold transition-all ${activeTab === 'ships-log' ? 'bg-lcars-blue text-black' : 'bg-lcars-tan text-black'}`}
                          onClick={() => { setActiveTab('ships-log'); setIsMenuOpen(false); }}
                        >
                          MONTHLY REPORT
                        </button>
                      )}
                      {hasInventoryAccess && (
                        <button 
                          className={`h-12 lcars-pill text-right pr-8 text-lg font-bold transition-all ${activeTab === 'con-resources' ? 'bg-lcars-blue text-black' : 'bg-lcars-gold text-black'}`}
                          onClick={() => { setActiveTab('con-resources'); setIsMenuOpen(false); }}
                        >
                          INVENTORY & SCENES
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <button 
                        onClick={() => setIsAdminMenuOpen(!isAdminMenuOpen)}
                        className={`h-12 lcars-pill text-right pr-8 text-lg font-bold transition-all bg-lcars-pink text-black hover:opacity-80`}
                      >
                        ADMIN {isAdminMenuOpen ? '▼' : '▶'}
                      </button>
                      {isAdminMenuOpen && (
                        <>
                          {isDLead && (
                            <button 
                              className={`h-12 lcars-pill text-right pr-8 text-lg font-bold transition-all ${activeTab === 'ships-log' ? 'bg-lcars-blue text-black' : 'bg-lcars-tan text-black'}`}
                              onClick={() => { setActiveTab('ships-log'); setIsMenuOpen(false); }}
                            >
                              MONTHLY REPORT
                            </button>
                          )}
                          {hasCoreAdminAccess && (
                            <>
                              <button 
                                className={`h-12 lcars-pill text-right pr-8 text-lg font-bold transition-all ${activeTab === 'admin-events' ? 'bg-lcars-blue text-black' : 'bg-lcars-tan text-black'}`}
                                onClick={() => { setActiveTab('admin-events'); setIsMenuOpen(false); }}
                              >
                                MISSION ADMIN
                              </button>
                              <button 
                                className={`h-12 lcars-pill text-right pr-8 text-lg font-bold transition-all ${activeTab === 'admin' ? 'bg-lcars-blue text-black' : 'bg-lcars-tan text-black'}`}
                                onClick={() => { setActiveTab('admin'); setIsMenuOpen(false); }}
                              >
                                COMMAND DECK
                              </button>
                            </>
                          )}
                          {hasInventoryAccess && (
                            <button 
                              className={`h-12 lcars-pill text-right pr-8 text-lg font-bold transition-all ${activeTab === 'con-resources' ? 'bg-lcars-blue text-black' : 'bg-lcars-gold text-black'}`}
                              onClick={() => { setActiveTab('con-resources'); setIsMenuOpen(false); }}
                            >
                              INVENTORY & SCENES
                            </button>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* External Links & Help */}
              <div className="flex flex-col gap-2 mt-2">
                <button
                  onClick={() => { setActiveTab('documents'); setIsMenuOpen(false); }}
                  className={`h-12 lcars-pill text-right pr-8 text-lg font-bold transition-all ${activeTab === 'documents' ? 'bg-lcars-blue text-black' : 'bg-lcars-tan text-black'}`}
                >
                  OFFICIAL DOCUMENTS
                </button>
                <button
                  onClick={() => { setIsHowDoIModalOpen(true); setIsMenuOpen(false); }}
                  className="h-12 lcars-pill text-right pr-8 text-lg font-bold transition-all bg-lcars-orange text-black hover:opacity-80"
                >
                  HOW DO I...?
                </button>
              </div>

              {/* Profile & Main Tabs */}
              <button 
                className={`h-12 lcars-pill text-right pr-8 text-lg font-bold transition-all mt-2 ${activeTab === 'events' ? 'bg-lcars-blue text-black' : 'bg-lcars-tan text-black'}`}
                onClick={() => { setActiveTab('events'); setIsMenuOpen(false); }}
              >
                MEMORY ALPHA
              </button>
              <button 
                className={`h-12 lcars-pill text-right pr-8 text-lg font-bold transition-all mt-1 ${activeTab === 'archives' ? 'bg-lcars-blue text-black' : 'bg-lcars-tan text-black'}`}
                onClick={() => { setActiveTab('archives'); setIsMenuOpen(false); }}
              >
                SHIP'S LOGS
              </button>
              <button 
                className={`h-12 lcars-pill text-right pr-8 text-lg font-bold transition-all mt-1 ${activeTab === 'surveys' ? 'bg-lcars-blue text-black' : 'bg-lcars-tan text-black'}`}
                onClick={() => { setActiveTab('surveys'); setIsMenuOpen(false); }}
              >
                SURVEYS & LINKS
              </button>
              <button 
                className={`h-12 lcars-pill text-right pr-8 text-lg font-bold transition-all mt-1 ${activeTab === 'profile' ? 'bg-lcars-blue text-black' : 'bg-lcars-tan text-black'}`}
                onClick={() => { setActiveTab('profile'); setIsMenuOpen(false); }}
              >
                MY PROFILE
              </button>
            </div>

            <div className="mt-auto pb-6 max-w-md mx-auto w-full">
              <button 
                onClick={handleLogout}
                className="w-full h-12 lcars-pill bg-lcars-red text-[var(--foreground)] flex items-center justify-center gap-3 text-lg font-bold"
              >
                <LogOut className="w-5 h-5" /> LOGOUT
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Left Sidebar - LCARS Navigation (Desktop & Tablet) */}
      <aside className="hidden md:flex w-72 lg:w-80 flex-col gap-2 shrink-0">
        {/* Top Elbow */}
        <div className="h-32 shrink-0 bg-lcars-purple rounded-tl-[60px] relative">
          <div className="absolute bottom-0 right-0 w-32 h-16 bg-[var(--background)] rounded-tl-[40px]"></div>
          <div onClick={() => setActiveTab('comms')} className="absolute top-6 left-8 text-black font-bold tracking-tighter cursor-pointer hover:opacity-80 transition-opacity">
            <div className="text-3xl xl:text-4xl whitespace-nowrap overflow-hidden text-ellipsis pr-4">USS ZAVALA</div>
            <div className="text-sm tracking-[0.2em] xl:tracking-[0.3em] opacity-80">NCC-74709</div>
            <div className="text-[0.6rem] opacity-60 font-mono tracking-widest mt-1">{APP_VERSION}</div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex flex-col gap-1.5 pr-4 mt-12">
          <button 
            className={`h-10 lcars-pill text-right pr-8 transition-all whitespace-nowrap ${activeTab === 'comms' ? 'bg-lcars-blue text-black font-bold' : 'bg-lcars-gold text-black hover:bg-lcars-blue hover:text-black'}`}
            onClick={() => setActiveTab('comms')}
          >
            HOME / COMMS {(hasNewAnnouncements || hasNewTenForward) && '*'}
          </button>
          <button 
            className={`h-10 lcars-pill text-right pr-8 transition-all whitespace-nowrap ${activeTab === 'planner' ? 'bg-lcars-blue text-black' : 'bg-lcars-tan text-black hover:bg-lcars-blue hover:text-black'}`}
            onClick={() => setActiveTab('planner')}
          >
            CALENDAR
          </button>
          {isConPlanner && (
            <>
              <button 
                className={`h-10 lcars-pill text-right pr-8 transition-all whitespace-nowrap ${activeTab === 'con-planner' ? 'bg-lcars-blue text-black' : 'bg-lcars-gold text-black hover:bg-lcars-blue hover:text-black'}`}
                onClick={() => setActiveTab('con-planner')}
              >
                CON DUTY ROSTER
              </button>
            </>
          )}

          {/* Admin Tools Group */}
          {hasAnyAdminAccess && isOnZavala && (
            <div className="mt-4 flex flex-col gap-1.5">
              {adminItemsCount === 1 ? (
                <>
                  {isDLead && (
                    <button 
                      className={`h-10 lcars-pill text-right pr-8 transition-all whitespace-nowrap ${activeTab === 'ships-log' ? 'bg-lcars-blue text-black' : 'bg-lcars-tan text-black hover:bg-lcars-blue hover:text-black'}`}
                      onClick={() => setActiveTab('ships-log')}
                    >
                      MONTHLY REPORT
                    </button>
                  )}
                  {hasInventoryAccess && (
                    <button 
                      className={`h-10 lcars-pill text-right pr-8 transition-all whitespace-nowrap ${activeTab === 'con-resources' ? 'bg-lcars-blue text-black' : 'bg-lcars-gold text-black hover:bg-lcars-blue hover:text-black'}`}
                      onClick={() => setActiveTab('con-resources')}
                    >
                      INVENTORY & SCENES
                    </button>
                  )}
                </>
              ) : (
                <>
                  <button 
                    onClick={() => setIsAdminMenuOpen(!isAdminMenuOpen)}
                    className={`h-10 lcars-pill text-right pr-8 transition-all whitespace-nowrap bg-lcars-pink text-black hover:opacity-80`}
                  >
                    ADMIN {isAdminMenuOpen ? '▼' : '▶'}
                  </button>
                  {isAdminMenuOpen && (
                    <>
                      {isDLead && (
                        <button 
                          className={`h-10 lcars-pill text-right pr-8 transition-all whitespace-nowrap ${activeTab === 'ships-log' ? 'bg-lcars-blue text-black' : 'bg-lcars-tan text-black hover:bg-lcars-blue hover:text-black'}`}
                          onClick={() => setActiveTab('ships-log')}
                        >
                          MONTHLY REPORT
                        </button>
                      )}
                      {hasCoreAdminAccess && (
                        <>
                          <button 
                            className={`h-10 lcars-pill text-right pr-8 transition-all whitespace-nowrap ${activeTab === 'admin-events' ? 'bg-lcars-blue text-black' : 'bg-lcars-tan text-black hover:bg-lcars-blue hover:text-black'}`}
                            onClick={() => setActiveTab('admin-events')}
                          >
                            MISSION ADMIN
                          </button>
                          <button 
                            className={`h-10 lcars-pill text-right pr-8 transition-all whitespace-nowrap ${activeTab === 'admin' ? 'bg-lcars-blue text-black' : 'bg-lcars-tan text-black hover:bg-lcars-blue hover:text-black'}`}
                            onClick={() => setActiveTab('admin')}
                          >
                            COMMAND DECK
                          </button>
                        </>
                      )}
                      {hasInventoryAccess && (
                        <button 
                          className={`h-10 lcars-pill text-right pr-8 transition-all whitespace-nowrap ${activeTab === 'con-resources' ? 'bg-lcars-blue text-black' : 'bg-lcars-gold text-black hover:bg-lcars-blue hover:text-black'}`}
                          onClick={() => setActiveTab('con-resources')}
                        >
                          INVENTORY & SCENES
                        </button>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* External Links */}
          <div className="flex flex-col gap-2 mt-4">
            <button
              onClick={() => setActiveTab('documents')}
              className={`h-10 lcars-pill text-right pr-8 transition-all whitespace-nowrap ${activeTab === 'documents' ? 'bg-lcars-blue text-black' : 'bg-lcars-tan text-black hover:bg-lcars-blue hover:text-black'}`}
            >
              OFFICIAL DOCUMENTS
            </button>
            <button
              onClick={() => setIsHowDoIModalOpen(true)}
              className="h-10 lcars-pill text-right pr-8 transition-all whitespace-nowrap bg-lcars-orange text-black hover:opacity-80"
            >
              HOW DO I...?
            </button>
          </div>

          {/* Profile at the bottom of the main list */}
          <button 
            className={`h-10 lcars-pill text-right pr-8 transition-all whitespace-nowrap mt-4 ${activeTab === 'events' ? 'bg-lcars-blue text-black' : 'bg-lcars-tan text-black hover:bg-lcars-blue hover:text-black'}`}
            onClick={() => setActiveTab('events')}
          >
            MEMORY ALPHA
          </button>
          <button 
            className={`h-10 lcars-pill text-right pr-8 transition-all whitespace-nowrap mt-2 ${activeTab === 'archives' ? 'bg-lcars-blue text-black' : 'bg-lcars-tan text-black hover:bg-lcars-blue hover:text-black'}`}
            onClick={() => setActiveTab('archives')}
          >
            SHIP'S LOGS
          </button>
          <button 
            className={`h-10 lcars-pill text-right pr-8 transition-all whitespace-nowrap mt-2 ${activeTab === 'surveys' ? 'bg-lcars-blue text-black' : 'bg-lcars-tan text-black hover:bg-lcars-blue hover:text-black'}`}
            onClick={() => setActiveTab('surveys')}
          >
            SURVEYS & LINKS
          </button>
          <button 
            className={`h-10 lcars-pill text-right pr-8 transition-all whitespace-nowrap mt-2 ${activeTab === 'profile' ? 'bg-lcars-blue text-black' : 'bg-lcars-tan text-black hover:bg-lcars-blue hover:text-black'}`}
            onClick={() => setActiveTab('profile')}
          >
            MY PROFILE
          </button>
          
          <div className="mt-4 space-y-1">
            <div className="h-8 bg-lcars-purple rounded-l-full"></div>
            <div className="h-16 bg-lcars-blue rounded-l-full"></div>
            <div className="h-8 bg-lcars-pink rounded-l-full"></div>
          </div>
        </div>

        {/* Bottom Elbow & Logout */}
        <div className="mt-auto flex flex-col gap-2">
          <div className="pb-2 pr-4">
            <button 
              onClick={handleLogout}
              className="w-full h-10 lcars-pill bg-lcars-red text-[var(--foreground)] hover:opacity-80 flex items-center justify-center gap-2"
            >
              <LogOut className="w-4 h-4" /> LOGOUT
            </button>
          </div>
          <div className="h-28 bg-lcars-gold rounded-bl-[60px] relative">
            <div className="absolute top-0 right-0 w-32 h-14 bg-[var(--background)] rounded-bl-[40px]"></div>
            <div className="absolute bottom-6 left-8 text-black font-bold text-sm tracking-widest leading-tight">
              SYSTEM<br/>
              <span className="text-lg">STATUS: OK</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col gap-2 overflow-hidden">
        {/* Top Header Bar (Desktop & Tablet) */}
        <header className="hidden md:flex h-12 items-center gap-2 shrink-0">
          <div className="h-full w-32 bg-lcars-purple rounded-tr-[30px]"></div>
          <div className="flex-1 h-full bg-lcars-gold flex items-center justify-between px-6">
            <div className="flex items-center gap-4">
              <div className="text-black font-bold text-xl tracking-widest">
                NCC-74709 | COMPUTER COMMAND CENTER
              </div>
              <select 
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
                className="bg-[var(--background)]/20 text-black border-2 border-black/40 rounded-full px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-widest outline-none"
              >
                <option value="trek-classic">Trek Classic</option>
                <option value="theme-delta">Delta Quadrant</option>
                <option value="theme-sovereign">Sovereign Protocol</option>
                <option value="theme-professional">Professional</option>
                <option value="theme-light">Light Mode</option>
                <option value="theme-ada">ADA Compliant (Light)</option>
              </select>
              <select 
                value={textSize}
                onChange={(e) => setTextSize(e.target.value)}
                className="bg-[var(--background)]/20 text-black border-2 border-black/40 rounded-full px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-widest outline-none"
              >
                <option value="normal">Normal Text</option>
                <option value="large">Large Text</option>
                <option value="xlarge">Extra Large Text</option>
              </select>
              <button
                onClick={() => toggleSimpleMode(true)}
                className="bg-[#238636] hover:bg-[#2ea043] text-white border-2 border-black/40 rounded-full px-3 py-1 text-[0.6rem] font-bold uppercase tracking-widest transition-all flex items-center gap-1 shadow-sm"
              >
                <span>🟢</span> EASY VIEW
              </button>
              <button
                onClick={() => setIsHelpModalOpen(true)}
                className="bg-lcars-blue text-black border-2 border-black/40 rounded-full px-4 py-1 text-[0.6rem] font-bold uppercase tracking-widest hover:bg-white transition-all flex items-center gap-1"
              >
                <HelpCircle className="w-3 h-3" /> HELP
              </button>
            </div>
            <div className="text-black font-bold text-sm">
              {profile?.firstName} {profile?.lastName} | {isGuest ? 'GUEST' : (allRanks.find(r => r.id === profile?.currentRank || (profile?.currentRank === 'CDR' && r.id === 'CMDR'))?.shortName || profile?.currentRank)}
            </div>
          </div>
          <div className="h-full w-16 bg-lcars-purple rounded-l-full"></div>
        </header>

        {/* Content Container */}
        <main className="flex-1 overflow-auto bg-[var(--background)] border-l-4 lg:border-l-8 border-lcars-blue pl-1.5 lg:pl-4">
          <div className="max-w-7xl mx-auto h-full py-1.5 lg:py-4">
            <Suspense fallback={<div className="flex justify-center p-8 text-lcars-blue font-lcars animate-pulse text-2xl tracking-widest">LOADING CORE SYSTEM...</div>}>
            {activeTab === 'comms' && (
              <Communications 
                currentUserProfile={profile} 
                allUsers={allUsers} 
                hasNewAnnouncements={hasNewAnnouncements}
                hasNewTenForward={hasNewTenForward}
                onClearAnnouncements={() => {
                  localStorage.setItem('lastViewedAnnouncements', Date.now().toString());
                  setHasNewAnnouncements(false);
                }}
                onClearTenForward={() => {
                  localStorage.setItem('lastViewedTenForward', Date.now().toString());
                  setHasNewTenForward(false);
                }}
              />
            )}
            {activeTab === 'events' && (
              <EventList 
                events={visibleEvents} 
                attendance={allAttendance} 
                currentUser={profile!} 
              />
            )}
            {activeTab === 'planner' && (
              <MissionPlanner 
                events={visibleEvents} 
                attendance={allAttendance} 
                userProfile={profile} 
                onTabChange={setActiveTab}
                onSelectConvention={setSelectedConIdForPlanner}
              />
            )}
            {activeTab === 'profile' && (
              <ProfileView 
                user={profile!} 
                events={visibleEvents} 
                attendance={allAttendance} 
                ranks={allRanks}
                departments={allDepartments}
              />
            )}
            {activeTab === 'con-planner' && (
              <ConPlannerMain 
                currentUserProfile={profile} 
                initialConId={selectedConIdForPlanner}
                onClearInitialCon={() => setSelectedConIdForPlanner(null)}
                isPublicMode={true}
              />
            )}
            {activeTab === 'con-resources' && (
              <ConResourcesMain />
            )}
            {activeTab === 'documents' && (
              <div className="h-full w-full bg-[var(--background)] border-2 border-lcars-tan rounded-xl overflow-hidden flex flex-col">
                <div className="bg-lcars-tan text-[var(--foreground)] p-4 lcars-text-large flex justify-between items-center">
                  <span>OFFICIAL DOCUMENTS</span>
                </div>
                <iframe 
                  src="https://drive.google.com/embeddedfolderview?id=1UYh-igZYhumJavzrel-aQs_YdGpvY1wO#list"
                  width="100%" 
                  height="100%" 
                  className="w-full flex-1 border-0 bg-white"
                  style={{ colorScheme: 'light' }}
                  title="Official Documents"
                ></iframe>
              </div>
            )}
            {activeTab === 'ships-log' && (
              <ShipsLogMain 
                currentUserProfile={profile} 
                isAdmin={hasAdminAccess} 
                isDeptLead={isDeptLead(profile)} 
                events={allEvents} 
                users={allUsers}
                attendance={allAttendance}
                ranks={allRanks}
              />
            )}
            {activeTab === 'archives' && (
              <Suspense fallback={<div className="flex justify-center p-8 text-lcars-blue animate-pulse">LOADING...</div>}>
                <ArchiveViewer />
              </Suspense>
            )}
            {activeTab === 'surveys' && (
              <Suspense fallback={<div className="flex justify-center p-8 text-lcars-blue animate-pulse">LOADING...</div>}>
                <SurveyList />
              </Suspense>
            )}
            {activeTab === 'admin' && hasAdminAccess && (
              <AdminDashboard 
                users={allUsers} 
                events={allEvents} 
                attendance={allAttendance} 
                currentUserProfile={profile}
                ranks={allRanks}
                departments={allDepartments}
                initialTab="members"
                onTabChange={setActiveTab}
                onSelectConvention={setSelectedConIdForPlanner}
              />
            )}
            {activeTab === 'admin-events' && hasAdminAccess && (
              <AdminDashboard 
                users={allUsers} 
                events={allEvents} 
                attendance={allAttendance} 
                currentUserProfile={profile}
                ranks={allRanks}
                departments={allDepartments}
                initialTab="events"
                onTabChange={setActiveTab}
                onSelectConvention={setSelectedConIdForPlanner}
              />
            )}
            </Suspense>
          </div>
        </main>

        {/* Footer Bar */}
        <footer className="h-8 flex items-center gap-2 shrink-0">
          <div className="h-full w-12 lg:w-48 bg-lcars-blue rounded-r-full"></div>
          <div className="flex-1 h-full bg-lcars-purple flex items-center px-4 lg:px-6">
            <div className="text-black font-bold text-[0.6rem] lg:text-[0.7rem] tracking-[0.2em] lg:tracking-[0.3em] truncate">
              COMPUTER CORE STATUS: NOMINAL | STARFLEET INTERNATIONAL
            </div>
          </div>
          <div className="h-full w-12 lg:w-32 bg-lcars-blue rounded-l-full"></div>
        </footer>
      </div>

      <Suspense fallback={null}>
        {isHelpModalOpen && (
          <HelpReporterModal 
            onClose={() => setIsHelpModalOpen(false)} 
            currentUserProfile={profile} 
            appVersion={APP_VERSION}
          />
        )}

        <HowDoIModal
          isOpen={isHowDoIModalOpen}
          onClose={() => setIsHowDoIModalOpen(false)}
          profile={profile}
        />

        {profile && (
          <LoginAlertsModal
            isOpen={showAlertModal}
            onClose={() => setShowAlertModal(false)}
            profile={profile}
            activeAlerts={activeAlerts}
            onDismissAlert={handleDismissAlert}
          />
        )}

        <LCARSVoiceAssistant
          onNavigateTab={(tab) => {
            setIsSimpleMode(false);
            setActiveTab(tab);
          }}
          onToggleSimpleMode={toggleSimpleMode}
          isSimpleMode={isSimpleMode}
          currentUserRank={allRanks.find(r => r.id === profile?.currentRank)?.name || profile?.currentRank}
          currentUserName={profile ? `${profile.firstName} ${profile.lastName}` : undefined}
          nextEventTitle={allEvents[0]?.name}
          nextEventDate={allEvents[0]?.startDate}
        />
      </Suspense>
    </div>
  );
}
